import { safeError } from "../../utils/safeError";
import { useState, useEffect, useCallback } from "react";
import { Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { getUser, logout, getAuthToken, ROLES } from "../../lib/auth";
import { API_URL, formatCurrency } from "../../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Radio,
  CreditCard,
  Settings,
  LogOut,
  Menu,
  X,
  TrendingUp,
  Users,
  Clock,
  DollarSign,
  Download,
  ChevronRight,
  Plus,
  BarChart3,
  FileText,
  AlertTriangle,
  CheckCircle,
  Check,
  Phone,
  Calendar,
  Zap,
  Wallet,
  Copy,
  Ticket,
  RefreshCw,
  Ban,
  Wifi,
  Printer,
} from "lucide-react";
import { CaiwaveLogo } from "../../components/CaiwaveLogo";
import HotspotLocationFields from "../../components/HotspotLocationFields";
import HotspotLocationEditor from "../../components/HotspotLocationEditor";
import HotspotLocationSummary from "../../components/HotspotLocationSummary";
import PaymentSettings from "./PaymentSettings";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";

// Subscription Status Banner Component
const SubscriptionBanner = ({ subscription, onPayNow }) => {
  if (!subscription) return null;

  const { subscription_status, trial_days_remaining, current_invoice, monthly_fee } = subscription;

  const getStatusConfig = () => {
    switch (subscription_status) {
      case "trial":
        return {
          bg: "bg-blue-500/10 border-blue-500/30",
          icon: <Clock className="w-5 h-5 text-blue-400" />,
          title: `Free Trial - ${trial_days_remaining} days remaining`,
          description: `Your trial ends in ${trial_days_remaining} days. Pay KES ${monthly_fee} to continue after trial.`,
          showPayButton: trial_days_remaining <= 3,
          urgent: false
        };
      case "active":
        return {
          bg: "bg-green-500/10 border-green-500/30",
          icon: <CheckCircle className="w-5 h-5 text-green-400" />,
          title: "Subscription Active",
          description: "Your subscription is active. Enjoy all features!",
          showPayButton: false,
          urgent: false
        };
      case "grace_period":
        return {
          bg: "bg-yellow-500/10 border-yellow-500/30",
          icon: <AlertTriangle className="w-5 h-5 text-yellow-400" />,
          title: "Payment Overdue",
          description: `Your trial has ended. Pay KES ${monthly_fee} now to avoid suspension.`,
          showPayButton: true,
          urgent: true
        };
      case "suspended":
        return {
          bg: "bg-red-500/10 border-red-500/30",
          icon: <AlertTriangle className="w-5 h-5 text-red-400" />,
          title: "Account Suspended",
          description: `Your hotspot is suspended due to non-payment. Pay KES ${monthly_fee} to reactivate.`,
          showPayButton: true,
          urgent: true
        };
      case "lifetime":
        return {
          bg: "bg-purple-500/10 border-purple-500/30",
          icon: <CheckCircle className="w-5 h-5 text-purple-400" />,
          title: "Lifetime Access",
          description: "You have lifetime access. No subscription fees required!",
          showPayButton: false,
          urgent: false
        };
      default:
        return null;
    }
  };

  const config = getStatusConfig();
  if (!config) return null;

  return (
    <div className={`p-4 rounded-xl border ${config.bg} mb-6`} data-testid="subscription-banner">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          {config.icon}
          <div>
            <h3 className="font-semibold">{config.title}</h3>
            <p className="text-sm text-neutral-400">{config.description}</p>
          </div>
        </div>
        {config.showPayButton && current_invoice && (
          <Button
            onClick={() => onPayNow(current_invoice)}
            className={config.urgent ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}
            data-testid="pay-now-btn"
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Pay KES {monthly_fee} Now
          </Button>
        )}
      </div>
    </div>
  );
};

// Payment Modal Component (Paystack)
const PaymentModal = ({ invoice, onClose, onSuccess }) => {
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);

  const handlePay = async () => {
    if (!phone || phone.length < 9) {
      toast.error("Please enter a valid phone number");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/invoices/pay/${invoice.id}`,
        { phone_number: `254${phone}` },
        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      );

      if (response.data.success) {
        if (response.data.authorization_url) {
          // Redirect to Paystack payment page
          toast.success("Redirecting to payment page...");
          window.open(response.data.authorization_url, "_blank");
          onClose();
          // Show instructions
          toast.info("Complete payment in the new tab, then refresh this page");
        } else {
          toast.success(response.data.message);
          onSuccess();
          onClose();
        }
      } else {
        toast.error(response.data.message || "Payment failed");
      }
    } catch (error) {
      toast.error(safeError(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-neutral-900 rounded-xl max-w-md w-full p-6 border border-neutral-800">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Pay Subscription</h2>
          <button onClick={onClose} className="p-2 hover:bg-neutral-800 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 bg-neutral-800 rounded-lg mb-6">
          <div className="flex justify-between items-center">
            <span className="text-neutral-400">Invoice:</span>
            <span className="font-mono text-sm">{invoice.invoice_number}</span>
          </div>
          <div className="flex justify-between items-center mt-2">
            <span className="text-neutral-400">Hotspots:</span>
            <span>{invoice.hotspot_count}</span>
          </div>
          <div className="flex justify-between items-center mt-2">
            <span className="text-neutral-400">Amount:</span>
            <span className="font-bold text-2xl text-green-400">KES {invoice.amount}</span>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm text-neutral-400 mb-2">Phone Number (for payment confirmation)</label>
          <div className="flex items-center bg-neutral-800 border border-neutral-700 rounded-lg">
            <span className="px-4 text-neutral-500">+254</span>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 9))}
              className="flex-1 bg-transparent px-2 py-3 focus:outline-none"
              placeholder="724825975"
              data-testid="subscription-phone-input"
            />
          </div>
          <p className="text-xs text-neutral-500 mt-2">
            You'll be redirected to Paystack to complete payment via M-Pesa or Card
          </p>
        </div>

        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1 border-neutral-700">
            Cancel
          </Button>
          <Button
            onClick={handlePay}
            disabled={loading || !phone}
            className="flex-1 bg-green-600 hover:bg-green-700"
            data-testid="confirm-subscription-payment"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <CreditCard className="w-4 h-4 mr-2" />
                Pay with Paystack
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Dashboard Overview Component
const DashboardOverview = () => {
  const [stats, setStats] = useState(null);
  const [hotspots, setHotspots] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payingInvoice, setPayingInvoice] = useState(null);
  const user = getUser();


  const fetchData = useCallback(async () => {
    try {
      const token = getAuthToken();
      const headers = { Authorization: `Bearer ${token}` };

      const [statsRes, hotspotsRes, subscriptionRes] = await Promise.all([
        axios.get(`${API_URL}/analytics/dashboard`, { headers }),
        axios.get(`${API_URL}/hotspots/`, { headers }),
        axios.get(`${API_URL}/subscriptions/status`, { headers }),
      ]);
      setStats(statsRes.data);
      setHotspots(hotspotsRes.data);
      setSubscription(subscriptionRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
    }, []);


  useEffect(() => {
    fetchData();

    const interval = setInterval(() => {
      fetchData();
    }, 15000);

    return () => clearInterval(interval);
  }, [fetchData]);

  // Mock chart data
  const chartData = [
    { name: "Mon", revenue: 1200, sessions: 45 },
    { name: "Tue", revenue: 1800, sessions: 62 },
    { name: "Wed", revenue: 2200, sessions: 78 },
    { name: "Thu", revenue: 1950, sessions: 71 },
    { name: "Fri", revenue: 2800, sessions: 95 },
    { name: "Sat", revenue: 3200, sessions: 112 },

    { name: "Sun", revenue: 2600, sessions: 88 },
  ];

  const renderDiagnosticItem = (label, passed, detail = "") => (
    <div className="flex items-start gap-2 text-sm">
      <span className={passed ? "text-green-400" : "text-yellow-400"}>
        {passed ? "✓" : "⚠"}
      </span>
      <div>
        <p className={passed ? "text-neutral-200" : "text-neutral-300"}>{label}</p>
        {detail && <p className="text-xs text-neutral-500">{detail}</p>}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="owner-dashboard">
      {/* Subscription Status Banner */}
      <SubscriptionBanner
        subscription={subscription}
        onPayNow={(invoice) => setPayingInvoice(invoice)}
      />

      {/* Welcome Header */}
      <div>
        <h1 className="text-2xl font-bold">Welcome back, {user?.name}</h1>
        <p className="text-neutral-400 mt-1">Here&apos;s your hotspot performance overview</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="stat-card stat-card-success">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Total Revenue</p>
              <p className="text-2xl font-bold mt-1">
                {formatCurrency(stats?.total_revenue || 0)}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-500" strokeWidth={1.5} />
            </div>
          </div>
          <p className="text-green-400 text-sm mt-3 flex items-center gap-1">
            <TrendingUp className="w-4 h-4" />
            +12.5% from last week
          </p>
        </div>

        <div className="stat-card stat-card-primary">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Active Hotspots</p>
              <p className="text-2xl font-bold mt-1">
                {stats?.active_hotspots || 0} / {stats?.total_hotspots || 0}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
              <Radio className="w-6 h-6 text-blue-500" strokeWidth={1.5} />
            </div>
          </div>
        </div>

        <div className="stat-card stat-card-warning">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Total Sessions</p>
              <p className="text-2xl font-bold mt-1">{stats?.total_sessions || 0}</p>
            </div>
            <div className="w-12 h-12 bg-yellow-500/10 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-yellow-500" strokeWidth={1.5} />
            </div>
          </div>
        </div>

        <div className="stat-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-neutral-400 text-sm">Your Share (70%)</p>
              <p className="text-2xl font-bold mt-1">
                {formatCurrency((stats?.total_revenue || 0) * 0.7)}
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-purple-500" strokeWidth={1.5} />
            </div>
          </div>
        </div>
      </div>

      {/* Chart & Recent Activity */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Revenue Chart */}
        <div className="lg:col-span-2 dashboard-card p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-semibold">Revenue Overview</h2>
            <select className="bg-neutral-800 border border-neutral-700 rounded-md px-3 py-1 text-sm">
              <option>Last 7 days</option>
              <option>Last 30 days</option>
            </select>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                <XAxis dataKey="name" stroke="#71717a" fontSize={12} />
                <YAxis stroke="#71717a" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#18181b",
                    border: "1px solid #27272a",
                    borderRadius: "8px",
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#2563eb"
                  fillOpacity={1}
                  fill="url(#colorRevenue)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="dashboard-card p-6">
          <h2 className="font-semibold mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <Link to="/owner/hotspots">
              <Button
                variant="outline"
                className="w-full justify-between border-neutral-700 hover:bg-neutral-800"
              >
                <span className="flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  Add Hotspot
                </span>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
            <Link to="/owner/payments">
              <Button
                variant="outline"
                className="w-full justify-between border-neutral-700 hover:bg-neutral-800"
              >
                <span className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  View Payments
                </span>
                <ChevronRight className="w-4 h-4" />
              </Button>
            </Link>
            <Button
              variant="outline"
              className="w-full justify-between border-green-700 text-green-400 hover:bg-green-900/20"
            >
              <span className="flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                Withdraw Earnings
              </span>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Recent Hotspots */}
          <div className="mt-6 pt-6 border-t border-neutral-800">
            <h3 className="text-sm font-medium text-neutral-400 mb-3">Your Hotspots</h3>
            {hotspots.length === 0 ? (
              <p className="text-neutral-500 text-sm">No hotspots yet</p>
            ) : (
              <div className="space-y-2">
                {hotspots.slice(0, 3).map((hotspot) => (
                  <div
                    key={hotspot.id}
                    className="flex items-center justify-between p-2 rounded-md bg-neutral-800/50"
                  >
                    <div className="flex items-center gap-2">
                      <div
                        className={`w-2 h-2 rounded-full ${
                          hotspot.status === "active"
                            ? "bg-green-500"
                            : "bg-neutral-500"
                        }`}
                      />
                      <span className="text-sm">{hotspot.name}</span>
                    </div>
                    <span className="text-xs text-neutral-400">
                      {formatCurrency(hotspot.total_revenue || 0)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Payment Modal */}
      {payingInvoice && (
        <PaymentModal
          invoice={payingInvoice}
          onClose={() => setPayingInvoice(null)}
          onSuccess={fetchData}
        />
      )}
    </div>
  );
};

// Hotspots Management Component
const HotspotsPage = () => {
  const [hotspots, setHotspots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingLocationHotspot, setEditingLocationHotspot] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    ssid: "",
    country_code: "KE",
    country_name: "Kenya",
    county: "",
    constituency: "",
    ward: "",
    location_name: "",
  });

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  useEffect(() => {
    fetchHotspots();
  }, []);

  const fetchHotspots = async () => {
    try {
      const response = await axios.get(`${API_URL}/hotspots/`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      setHotspots(response.data);
    } catch (error) {
      console.error("Failed to fetch hotspots:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const user = getUser();
      await axios.post(
        `${API_URL}/hotspots/`,
        {
          ...formData,
          owner_id: user.id,
        },
        {
          headers: { Authorization: `Bearer ${getAuthToken()}` },
        }
      );
      toast.success("Hotspot created successfully");
      setShowForm(false);
      setFormData({
        name: "",
        ssid: "",
        country_code: "KE",
        country_name: "Kenya",
        county: "",
        constituency: "",
        ward: "",
        location_name: "",
      });
      fetchHotspots();
    } catch (error) {
      toast.error(safeError(error));
    }
  };

  return (
    <div className="space-y-6" data-testid="hotspots-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">My Hotspots</h1>
          <p className="text-neutral-400 mt-1">Manage your WiFi hotspot locations</p>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="add-hotspot-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Hotspot
        </Button>
      </div>

      {/* Add Hotspot Form */}
      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="dashboard-card p-6 space-y-4"
          data-testid="hotspot-form"
        >
          <h3 className="font-semibold">New Hotspot</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-neutral-400">Hotspot Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-neutral-900 border border-neutral-700 rounded-md"
                placeholder="e.g., Shop WiFi"
                required
              />
            </div>
            <div>
              <label className="text-sm text-neutral-400">SSID</label>
              <input
                type="text"
                value={formData.ssid}
                onChange={(e) => setFormData({ ...formData, ssid: e.target.value })}
                className="w-full mt-1 px-3 py-2 bg-neutral-900 border border-neutral-700 rounded-md"
                placeholder="e.g., CAIWAVE_Shop"
                required
              />
            </div>
            <HotspotLocationFields
              value={formData}
              onChange={setFormData}
            />
          </div>
          <p className="text-sm text-neutral-500 mt-2">
            After creating your hotspot, go to "MikroTik" to configure your router.
          </p>
          <div className="flex gap-3 mt-4">
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
              Create Hotspot
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowForm(false)}
              className="border-neutral-700"
            >
              Cancel
            </Button>
          </div>
        </form>
      )}

      {editingLocationHotspot && (
        <HotspotLocationEditor
          hotspot={editingLocationHotspot}
          onClose={() => setEditingLocationHotspot(null)}
          onSaved={fetchHotspots}
        />
      )}

      {/* Hotspots Table */}
      <div className="dashboard-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : hotspots.length === 0 ? (
          <div className="p-8 text-center">
            <Radio className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
            <h3 className="font-semibold mb-2">No hotspots yet</h3>
            <p className="text-neutral-400 text-sm">
              Add your first hotspot to start earning
            </p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Hotspot ID</th>
                <th>SSID</th>
                <th>Location</th>
                <th>Status</th>
                <th>Sessions</th>
                <th>Revenue</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {hotspots.map((hotspot) => (
                <tr key={hotspot.id}>
                  <td className="font-medium">{hotspot.name}</td>
                  <td>
                    <div className="flex items-center gap-2">
                      <code className="font-mono text-xs text-blue-400">{hotspot.id}</code>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(hotspot.id)}
                        className="text-neutral-400 hover:text-white"
                        title="Copy hotspot ID"
                      >
                        <Copy className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                  <td className="font-mono text-sm text-neutral-400">{hotspot.ssid}</td>
                  <td>
                    <HotspotLocationSummary hotspot={hotspot} />
                  </td>
                  <td>
                    {(() => {
                      const isActive = hotspot.status === "active";

                      return (
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                            isActive ? "badge-active" : "badge-inactive"
                          }`}
                        >
                          <span
                            className={`w-1.5 h-1.5 rounded-full ${
                              isActive ? "bg-green-500" : "bg-neutral-500"
                            }`}
                          />
                          {isActive ? "Active" : "Inactive"}
                        </span>
                      );
                    })()}
                  </td>
                  <td>{hotspot.total_sessions || 0}</td>
                  <td className="font-medium">
                    {formatCurrency(hotspot.total_revenue || 0)}
                  </td>
                  <td>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditingLocationHotspot(hotspot)}
                    >
                      Edit Location
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// MikroTik Component
const MikroTikSetupPage = () => {

const getProvisioningProgress = (diagnostic) => {
  if (!diagnostic) {
    return {
      step: "registration",
      message: "Registering your MikroTik router with CAIWAVE.",
      score: 0,
    };
  }

  const checks = diagnostic.checks || {};
  const router = diagnostic.router || {};
  const discovery = diagnostic.discovery || {};
  const topologyStatus = String(
    diagnostic.configuration?.topology_status ||
    router.topology_status ||
    diagnostic.diagnostics?.topology_status ||
    ""
  ).toLowerCase();

  const discoveryReceived =
    Boolean(router.discovery_received) ||
    Boolean(discovery.received_at) ||
    Boolean(diagnostic.diagnostics?.discovery_received);

  // Discovery-first onboarding:
  // connection_confirmed is intentionally false until the router
  // completes the actual provisioning/verification lifecycle.
  if (!discoveryReceived) {
    return {
      step: "discovery",
      message:
        diagnostic.next_action ||
        "Waiting for CAIWAVE to receive router discovery information.",
      score: diagnostic.score || 0,
    };
  }

  // Discovery has arrived, but topology must be inferred and validated
  // before the final production artifact can be considered ready.
  if (
    topologyStatus &&
    !["inferred", "ready", "verified"].includes(topologyStatus)
  ) {
    return {
      step: "discovery",
      message:
        diagnostic.next_action ||
        "CAIWAVE is analyzing the discovered MikroTik topology.",
      score: diagnostic.score || 0,
    };
  }

  const provisioningChecks = [
    "bridge_configured",
    "lan_ports_configured",
    "dhcp_configured",
    "nat_configured",
    "hotspot_configured",
    "radius_client_configured",
    "heartbeat_scheduler_configured",
    "walled_garden_configured",
  ];

  const provisioningComplete = provisioningChecks.every(
    (key) => checks[key]
  );

  if (!provisioningComplete) {
    return {
      step: "provisioning",
      message:
        diagnostic.next_action ||
        "CAIWAVE is checking the router configuration.",
      score: diagnostic.score || 0,
    };
  }

  // The final artifact/configuration is ready, but real client
  // authentication and accounting still need to be verified.
  if (!checks.first_auth_success || !checks.accounting_seen) {
    return {
      step: "verification",
      message:
        diagnostic.next_action ||
        "Connect a client and complete a hotspot login to verify the installation.",
      score: diagnostic.score || 0,
    };
  }

  if (diagnostic.production_ready || diagnostic.score >= 100) {
    return {
      step: "ready",
      message: "Router is production-ready.",
      score: 100,
    };
  }

  return {
    step: "verification",
    message:
      diagnostic.next_action ||
      "CAIWAVE is completing the final verification checks.",
    score: diagnostic.score || 0,
  };
};

  const [hotspots, setHotspots] = useState([]);
  const [routers, setRouters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddRouter, setShowAddRouter] = useState(false);
  const [selectedHotspot, setSelectedHotspot] = useState("");
  const [routerName, setRouterName] = useState("");
  const [generatedScript, setGeneratedScript] = useState(null);
  const [routerActionBusy, setRouterActionBusy] = useState(null);
  const [rotatedRouterFiles, setRotatedRouterFiles] = useState({});
  const [generating, setGenerating] = useState(false);
  const [provisioningStep, setProvisioningStep] = useState("connect");
  const [provisioningMode, setProvisioningMode] = useState("fresh");
  const [provisioningRouter, setProvisioningRouter] = useState(null);
  const [provisioningMessage, setProvisioningMessage] = useState("");
  const [hotspotCidr, setHotspotCidr] = useState("10.10.0.1/24");
  const [dhcpPool, setDhcpPool] = useState("10.10.0.10-10.10.0.254");
  const [diagnostics, setDiagnostics] = useState({});
  const [timelines, setTimelines] = useState({});

  const fetchTimelines = useCallback(async (routerList) => {
    try {
      const token = getAuthToken();
      const headers = { Authorization: `Bearer ${token}` };

      const results = await Promise.all(
        routerList.map(async (router) => {
          try {
            const res = await axios.get(
              `${API_URL}/mikrotik-onboard/routers/${router.id}/timeline?limit=5`,
              { headers }
            );
            return [router.id, res.data.events || []];
          } catch (err) {
            console.error("Failed to fetch router timeline:", router.id, err);
            return [router.id, []];
          }
        })
      );

      setTimelines(Object.fromEntries(results));
    } catch (error) {
      console.error("Failed to fetch timelines:", error);
    }
    }, []);

  const fetchDiagnostics = useCallback(async (routerList) => {
    try {
      const token = getAuthToken();
      const headers = { Authorization: `Bearer ${token}` };

      const results = await Promise.all(
        routerList.map(async (router) => {
          try {
            const res = await axios.get(
              `${API_URL}/mikrotik-onboard/routers/${router.id}/diagnostics`,
              { headers }
            );
            return [router.id, res.data];
          } catch (err) {
            console.error("Failed to fetch router diagnostics:", router.id, err);
            return [router.id, null];
          }
        })
      );

      setDiagnostics(Object.fromEntries(results));
    } catch (error) {
      console.error("Failed to fetch diagnostics:", error);
    }
    },[]);

  const fetchData = useCallback(async () => {
    try {
      const token = getAuthToken();
      const headers = { Authorization: `Bearer ${token}` };

      const [hotspotsRes, routersRes] = await Promise.all([
        axios.get(`${API_URL}/hotspots/`, { headers }),
        axios.get(`${API_URL}/mikrotik-onboard/routers`, { headers }),
      ]);

      setHotspots(hotspotsRes.data);
      setRouters(routersRes.data);
      fetchDiagnostics(routersRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
    }, [fetchDiagnostics]);

  useEffect(() => {
    fetchData();

    const interval = setInterval(() => {
      fetchData();
    }, 15000);

    return () => clearInterval(interval);
    }, [fetchData]);
  const activeProvisioningDiagnostic = provisioningRouter
    ? diagnostics[provisioningRouter.router_id]
    : null;

  const provisioningProgress = getProvisioningProgress(
    activeProvisioningDiagnostic
  );

  const formatAge = (seconds) => {
    if (seconds === null || seconds === undefined) return "unknown";
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ago`;
  };

  const handleRegisterRouter = async () => {
    if (!selectedHotspot || !routerName.trim()) {
      toast.error("Please select a hotspot and enter router name");
      return;
    }

    setGenerating(true);
    try {
      const response = await axios.post(
        `${API_URL}/mikrotik-onboard/register`,

        {
          name: routerName.trim(),
          hotspot_id: selectedHotspot,
          mode: provisioningMode,
          create_bridge: true,
          bridge_name: "bridge-hotspot",
          hotspot_enabled: true,
          wifi_enabled: false,
          hotspot_cidr: hotspotCidr,
          dhcp_pool: dhcpPool,
          dns_name: "login.caiwave.local",
        },

        { headers: { Authorization: `Bearer ${getAuthToken()}` } }
      );

      setGeneratedScript(response.data);
      setProvisioningRouter(response.data);
      setProvisioningStep("discovery");
      setProvisioningMessage(
        "Router registered. CAIWAVE is preparing discovery and provisioning."
      );
      toast.success("Router onboarding started!");
      fetchData();
    } catch (error) {
      toast.error(safeError(error));
    } finally {
      setGenerating(false);
    }
  };


    const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

const downloadBootstrap = () => {
  const bootstrap = generatedScript?.bootstrap_rsc;

  if (!bootstrap?.content) {
    toast.error("CAIWAVE bootstrap is not available.");
    return;
  }

  const blob = new Blob([bootstrap.content], {
    type: "text/plain;charset=utf-8",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = bootstrap.filename || "caiwave-bootstrap.rsc";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  toast.success("CAIWAVE bootstrap downloaded.");
};

const rotateControlCredential = async (router) => {
  try {
    setRouterActionBusy(router.id);

    const response = await axios.post(
      `${API_URL}/mikrotik-onboard/routers/${router.id}/rotate-control-credential`,
      {},
      {
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
        },
      }
    );

    setRotatedRouterFiles((prev) => ({
      ...prev,
      [router.id]: {
        discovery_rsc: response.data.discovery_rsc,
        credential_initializer_rsc: response.data.credential_initializer_rsc,
        bootstrap_rsc: response.data.bootstrap_rsc,
      },
    }));

    toast.success(
      `${router.name}: new control credential generated.`
    );

    await fetchDiagnostics([router]);
  } catch (error) {
    toast.error(safeError(error));
  } finally {
    setRouterActionBusy(null);
  }
};

const downloadRouterFile = (file, fallbackName) => {
  if (!file?.content) {
    toast.error("File is not available.");
    return;
  }

  const blob = new Blob([file.content], {
    type: "text/plain;charset=utf-8",
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = file.filename || fallbackName;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  toast.success(`${file.filename || fallbackName} downloaded.`);
};

  const getStatusBadge = (status) => {
    const badges = {
      pending_configuration: { bg: "bg-yellow-500/10", text: "text-yellow-400", label: "Pending Setup" },
      configured: { bg: "bg-blue-500/10", text: "text-blue-400", label: "Configured" },
      connected: { bg: "bg-green-500/10", text: "text-green-400", label: "Connected" },
      offline: { bg: "bg-red-500/10", text: "text-red-400", label: "Offline" },
      error: { bg: "bg-red-500/10", text: "text-red-400", label: "Error" },
    };
    return badges[status] || { bg: "bg-gray-500/10", text: "text-gray-400", label: status };
  };

  const renderDiagnosticItem = (label, passed, detail = "") => (
    <div className="flex items-start gap-2 text-sm">
      <span className={passed ? "text-green-400" : "text-yellow-400"}>
        {passed ? "✓" : "⚠"}
      </span>
      <div>
        <p className={passed ? "text-neutral-200" : "text-neutral-300"}>{label}</p>
        {detail && <p className="text-xs text-neutral-500">{detail}</p>}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="mikrotik-setup-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">MikroTik</h1>
          <p className="text-neutral-400 mt-1">Configure your MikroTik routers for CAIWAVE integration</p>
        </div>
        <div className="flex gap-2">
          <a href="/setup" target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="border-blue-500 text-blue-400">
              Full Setup Wizard
            </Button>
          </a>
          <Button
            onClick={() => setShowAddRouter(true)}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="add-mikrotik-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add MikroTik
          </Button>
        </div>
      </div>

      {/* Guided Provisioning */}
      <div className="dashboard-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="font-semibold flex items-center gap-2">
              <Zap className="w-5 h-5 text-blue-400" />
              Guided Router Provisioning
            </h2>
            <p className="text-sm text-neutral-400 mt-1">
              CAIWAVE discovers your MikroTik, builds the correct plan, provisions it,
              and verifies the connection.
            </p>
          </div>

          <span className="text-xs px-2 py-1 rounded bg-blue-500/10 text-blue-400">
            Automated
          </span>
        </div>

        <div className="grid md:grid-cols-5 gap-3">
          {[
            ["1", "Choose Router", "New or existing"],
            ["2", "Discover", "Detect router capabilities"],
            ["3", "Plan", "CAIWAVE builds the plan"],
            ["4", "Provision", "Apply the plan"],
            ["5", "Verify", "Confirm everything works"],
          ].map(([number, title, description], index) => (
            <div
              key={number}
              className="relative p-4 bg-neutral-800/50 rounded-lg text-center border border-neutral-700"
            >
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center mx-auto mb-3 ${
                  index === 4
                    ? "bg-green-500/10"
                    : "bg-blue-500/10"
                }`}
              >
                {index === 4 ? (
                  <CheckCircle className="w-5 h-5 text-green-400" />
                ) : (
                  <span className="text-blue-400 font-bold">{number}</span>
                )}
              </div>

              <h4 className="font-medium text-sm">{title}</h4>
              <p className="text-xs text-neutral-500 mt-1">{description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Registered Routers */}
      <div className="dashboard-card">
        <h2 className="font-semibold mb-4">Registered Routers ({routers.length})</h2>

        {routers.length === 0 ? (
          <div className="text-center py-12">
            <Radio className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
            <p className="text-neutral-400">No routers registered yet</p>
            <Button
              onClick={() => setShowAddRouter(true)}
              variant="outline"
              className="mt-4 border-neutral-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Router
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {routers.map((router) => {
              const statusBadge = getStatusBadge(router.status);
              const hotspot = hotspots.find(h => h.id === router.hotspot_id);

              return (
                <div
                  key={router.id}
                  className="p-4 bg-neutral-800/50 rounded-lg border border-neutral-700"
                  data-testid={`router-${router.id}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-3 h-3 rounded-full ${router.connection_confirmed ? 'bg-green-400' : 'bg-yellow-400'}`} />
                      <h3 className="font-semibold">{router.name}</h3>
                      <span className={`text-xs px-2 py-1 rounded ${statusBadge.bg} ${statusBadge.text}`}>
                        {statusBadge.label}
                      </span>
                    </div>
                   <div className="flex items-center gap-2">
                     {router.topology_status === "inferred" &&
                       router.status === "ready_for_provisioning" && (
                         <span className="text-xs px-2 py-1 rounded bg-blue-500/10 text-blue-400">
                           Ready for Provisioning
                         </span>
                        )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-neutral-500">Hotspot</p>
                      <p className="font-medium">{hotspot?.name || "Unknown"}</p>
                    </div>
                    <div>
                      <p className="text-neutral-500">NAS Identifier</p>
                      <p className="font-mono text-xs">{router.nas_identifier}</p>
                    </div>
                    <div>
                      <p className="text-neutral-500">Created</p>
                      <p>{new Date(router.created_at).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <p className="text-neutral-500">Last Seen</p>
                      <p>{router.last_seen ? new Date(router.last_seen).toLocaleString() : "Never"}</p>
                    </div>
                  </div>

                  {diagnostics[router.id] && (
                    <div className="mt-4 p-4 bg-neutral-900/70 border border-neutral-800 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <h4 className="font-semibold">Router Diagnostics</h4>
                          <p className="text-xs text-neutral-500">{diagnostics[router.id].next_action}</p>
                          <p className="text-xs text-neutral-600 mt-1">
                            Auto-refreshing every 15s · heartbeat {formatAge(diagnostics[router.id].router?.heartbeat_age_seconds)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-bold text-blue-400">{diagnostics[router.id].score}%</p>
                          <p className="text-xs text-neutral-500">
                            {diagnostics[router.id].passed}/{diagnostics[router.id].total} checks
                          </p>
                          </div>
                        </div>

                      <div className="grid md:grid-cols-2 gap-3">
                        {renderDiagnosticItem("Heartbeat received", diagnostics[router.id].checks?.heartbeat_received)}
                        {renderDiagnosticItem("Heartbeat recent", diagnostics[router.id].checks?.heartbeat_recent)}
                        {renderDiagnosticItem("Router online", diagnostics[router.id].checks?.router_online)}
                        {renderDiagnosticItem("Connection confirmed", diagnostics[router.id].checks?.connection_confirmed)}
                        {renderDiagnosticItem("RADIUS configured", diagnostics[router.id].checks?.radius_configured)}
                        {renderDiagnosticItem("Paystack configured", diagnostics[router.id].checks?.paystack_configured)}
                        {renderDiagnosticItem("Bridge configured", diagnostics[router.id].checks?.bridge_configured)}
                        {renderDiagnosticItem("LAN ports configured", diagnostics[router.id].checks?.lan_ports_configured)}
                        {renderDiagnosticItem("DHCP configured", diagnostics[router.id].checks?.dhcp_configured)}
                        {renderDiagnosticItem("NAT configured", diagnostics[router.id].checks?.nat_configured)}
                        {renderDiagnosticItem("Hotspot configured", diagnostics[router.id].checks?.hotspot_configured)}
                        {renderDiagnosticItem("RADIUS client configured", diagnostics[router.id].checks?.radius_client_configured)}
                        {renderDiagnosticItem("Heartbeat scheduler configured", diagnostics[router.id].checks?.heartbeat_scheduler_configured)}
                        {renderDiagnosticItem("Walled garden configured", diagnostics[router.id].checks?.walled_garden_configured)}
                        {renderDiagnosticItem("First login seen", diagnostics[router.id].checks?.first_auth_seen)}
                        {renderDiagnosticItem("First login accepted", diagnostics[router.id].checks?.first_auth_success)}
                        {renderDiagnosticItem("Accounting seen", diagnostics[router.id].checks?.accounting_seen)}
                        {renderDiagnosticItem("Provisioning file generated", diagnostics[router.id].checks?.rsc_generated)}
                      </div>

                      {timelines[router.id]?.length > 0 && (
                        <div className="mt-4 border-t border-neutral-800 pt-3">
                          <h5 className="font-medium text-sm mb-2">Recent Timeline</h5>
                          <div className="space-y-2">
                            {timelines[router.id].map((event) => (
                              <div key={event.id} className="flex items-start gap-2 text-sm">
                                <span className={
                                  event.severity === "success" ? "text-green-400" :
                                  event.severity === "warning" ? "text-yellow-400" :
                                  event.severity === "error" ? "text-red-400" :
                                  "text-blue-400"
                                }>
                                  ●
                                </span>
                                <div>
                                  <p className="text-neutral-200">{event.title}</p>
                                  <p className="text-xs text-neutral-500">
                                    {new Date(event.timestamp).toLocaleString()} · {event.event_type}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="mt-3 flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-neutral-700"
                          onClick={() => fetchDiagnostics([router])}
                        >
                          Refresh Diagnostics
                        </Button>
                        {diagnostics[router.id].production_ready && (
                          <span className="text-xs px-2 py-1 rounded bg-green-500/10 text-green-400">
                            Production Ready
                          </span>
                        )}
                      </div>


                      <div className="mt-4 pt-4 border-t border-neutral-800">
                        <h5 className="font-medium text-sm mb-1">Router Actions</h5>

                        <p className="text-xs text-neutral-500 mb-3">
                          Manage the CAIWAVE control channel and generate fresh onboarding
                          files without manually configuring MikroTik interfaces.
                        </p>

                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-neutral-700"
                            disabled={routerActionBusy === router.id}
                            onClick={() => rotateControlCredential(router)}
                          >
                            {routerActionBusy === router.id
                              ? "Generating..."
                              : "Rotate Control Credential"}
                          </Button>

                          {rotatedRouterFiles[router.id]?.discovery_rsc?.content && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-neutral-700"
                              onClick={() =>
                                downloadRouterFile(
                                  rotatedRouterFiles[router.id].discovery_rsc,
                                  "caiwave-discovery.rsc"
                                )
                              }
                            >
                              Download Discovery Bootstrap
                            </Button>
                          )}

                          {rotatedRouterFiles[router.id]?.credential_initializer_rsc?.content && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-neutral-700"
                              onClick={() =>
                                downloadRouterFile(
                                  rotatedRouterFiles[router.id].credential_initializer_rsc,
                                  "caiwave-control-credential-init.rsc"
                                )
                              }
                            >
                              Download Control Credential Initializer
                            </Button>
                          )}

                          {rotatedRouterFiles[router.id]?.bootstrap_rsc?.content && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-neutral-700"
                              onClick={() =>
                                downloadRouterFile(
                                  rotatedRouterFiles[router.id].bootstrap_rsc,
                                  "caiwave-bootstrap.rsc"
                                )
                              }
                            >
                              Download CAIWAVE Bootstrap
                            </Button>
                          )}
                        </div>

                        {rotatedRouterFiles[router.id]?.discovery_rsc?.content && (
                          <p className="text-xs text-green-400 mt-3">
                            Fresh discovery bootstrap ready. Import the Discovery Bootstrap
                            once on the router to refresh topology.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

     </div>

      {/* Add Router Modal */}
      {showAddRouter && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-neutral-900 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-neutral-800">
            <div className="p-6 border-b border-neutral-800 flex items-center justify-between">
              <h2 className="text-xl font-semibold">Connect a MikroTik</h2>
              <button
                onClick={() => {
                  setShowAddRouter(false);
                  setGeneratedScript(null);
                  setRouterName("");
                  setSelectedHotspot("");
                }}
                className="p-2 hover:bg-neutral-800 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {!generatedScript ? (
                <>
                  {/* Router Name Input */}
                  <div>
                    <label className="block text-sm text-neutral-400 mb-2">Router Name</label>
                    <input
                      type="text"
                      value={routerName}
                      onChange={(e) => setRouterName(e.target.value)}
                      placeholder="e.g., Main Office Router"
                      className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                      data-testid="router-name-input"
                    />
                  </div>
                                    {/* Router Provisioning Mode */}
                  <div>
                    <label className="block text-sm text-neutral-400 mb-2">
                      Router Setup Type
                    </label>

                    <div className="grid grid-cols-2 gap-3">
                      <button
                        type="button"
                        onClick={() => setProvisioningMode("fresh")}
                        className={`p-4 rounded-lg border text-left transition ${
                          provisioningMode === "fresh"
                            ? "border-blue-500 bg-blue-500/10"
                            : "border-neutral-700 bg-neutral-800/50 hover:border-neutral-600"
                        }`}
                      >
                        <p className="font-medium">Fresh Router</p>
                        <p className="text-xs text-neutral-500 mt-1">
                          Clean/new MikroTik that CAIWAVE can provision.
                        </p>
                      </button>

                      <button
                        type="button"
                        onClick={() => setProvisioningMode("existing")}
                        className={`p-4 rounded-lg border text-left transition ${
                          provisioningMode === "existing"
                            ? "border-blue-500 bg-blue-500/10"
                            : "border-neutral-700 bg-neutral-800/50 hover:border-neutral-600"
                        }`}
                      >
                        <p className="font-medium">Existing Router</p>
                        <p className="text-xs text-neutral-500 mt-1">
                          Preserve the router and configure CAIWAVE integration.
                        </p>
                      </button>
                    </div>
                  </div>

                  {/* Hotspot Selection */}
                  <div>
                    <div className="mb-3 p-3 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-300 text-sm">
                      Recommended flow: this process links the MikroTik router to a specific hotspot ID in CAIWAVE.
                    </div>
                    <label className="block text-sm text-neutral-400 mb-2">Select Hotspot</label>
                    <select
                      value={selectedHotspot}
                      onChange={(e) => setSelectedHotspot(e.target.value)}
                      className="w-full bg-neutral-800 border border-neutral-700 rounded-lg px-4 py-3 focus:ring-2 focus:ring-blue-500"
                      data-testid="hotspot-select"
                    >
                      <option value="">Choose a hotspot...</option>
                      {hotspots.map((h) => (
                        <option key={h.id} value={h.id}>{h.name} - {h.location_name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Generate Button */}
                  <Button
                    onClick={handleRegisterRouter}
                    disabled={generating || !selectedHotspot || !routerName.trim()}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="generate-script-btn"
                  >
                    {generating ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Zap className="w-4 h-4 mr-2" />
                        Connect MikroTik
                      </>
                    )}
                  </Button>
                </>
                ) : (
                  <>
                    {/* Guided Provisioning Progress */}
                    <div className="space-y-6">

                      {/* Progress Header */}
                      <div>
                        <h3 className="text-lg font-semibold">
                          CAIWAVE Guided Provisioning
                        </h3>

                        <p className="text-sm text-neutral-500 mt-1">
                          CAIWAVE is guiding the router through registration,
                          discovery, provisioning, and verification.
                        </p>
                      </div>

                      {/* Progress Steps */}
                      <div className="space-y-3">
                        {[
                          {
                            key: "registration",
                            label: "Registration",
                            description: "Router registered with CAIWAVE",
                          },
                          {
                            key: "discovery",
                            label: "Discovery",
                            description: "Detecting router capabilities and interfaces",
                          },
                          {
                            key: "planning",
                            label: "Planning",
                            description: "Building the provisioning plan",
                          },
                          {
                            key: "provisioning",
                            label: "Provisioning",
                            description: "Applying the CAIWAVE configuration",
                          },
                          {
                            key: "verification",
                            label: "Verification",
                            description: "Checking router connectivity and services",
                          },
                          {
                            key: "ready",
                            label: "Ready",
                            description: "Router is ready for CAIWAVE",
                          },
                        ].map((step, index) => {
                          const stepOrder = [
                            "registration",
                            "discovery",
                            "planning",
                            "provisioning",
                            "verification",
                            "ready",
                          ];

                          const currentStep = provisioningProgress.step;
                          const currentIndex = stepOrder.indexOf(currentStep);
                          const stepIndex = stepOrder.indexOf(step.key);

                          const completed =
                            currentIndex > stepIndex ||
                            currentStep === "ready";

                          const active = currentStep === step.key;

                          return (
                            <div
                              key={step.key}
                              className={`flex items-start gap-4 p-4 rounded-lg border ${
                                active
                                  ? "border-blue-500/40 bg-blue-500/10"
                                  : completed
                                  ? "border-green-500/20 bg-green-500/5"
                                  : "border-neutral-800 bg-neutral-800/30"
                              }`}
                            >
                              <div
                                className={`flex items-center justify-center w-8 h-8 rounded-full shrink-0 ${
                                  completed
                                    ? "bg-green-500/20 text-green-400"
                                    : active
                                    ? "bg-blue-500/20 text-blue-400"
                                    : "bg-neutral-800 text-neutral-500"
                                }`}
                              >
                                {completed ? (
                                  <Check className="w-4 h-4" />
                                ) : (
                                  <span className="text-sm font-medium">
                                    {index + 1}
                                  </span>
                                )}
                              </div>

                              <div className="min-w-0">
                                <p className="font-medium">
                                  {step.label}
                                </p>

                                <p className="text-sm text-neutral-500 mt-1">
                                  {step.description}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                        {/* MikroTik Bootstrap */}
                        {generatedScript?.bootstrap_rsc?.content && (
                          <div className="p-5 rounded-lg border border-blue-500/30 bg-blue-500/10 space-y-5">
                            <div>
                              <h4 className="font-semibold text-blue-300">
                                Connect Your MikroTik
                              </h4>

                              <p className="text-sm text-neutral-300 mt-1">
                                CAIWAVE provides one bootstrap file. Import it once
                                in the MikroTik Terminal. It first discovers the
                                router topology, then establishes the CAIWAVE control
                                channel. No WAN or LAN port needs to be selected manually.
                              </p>
                            </div>

                            <div className="p-4 rounded-lg bg-neutral-900/60 border border-neutral-800 space-y-3">
                              <div>
                                <p className="text-xs uppercase tracking-wide text-neutral-500">
                                  Step 1
                                </p>

                                <h5 className="font-semibold text-neutral-200 mt-1">
                                  Import CAIWAVE Bootstrap
                                </h5>

                                <p className="text-sm text-neutral-400 mt-1">
                                  Import this single file in the MikroTik Terminal.
                                  Discovery runs first, followed by the persistent
                                  CAIWAVE control agent.
                                </p>
                              </div>

                              <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-neutral-950/50 border border-neutral-800">
                                <div className="min-w-0">
                                  <p className="text-xs text-neutral-500">
                                    CAIWAVE bootstrap
                                  </p>

                                  <p className="font-mono text-sm text-neutral-200 truncate">
                                    {generatedScript.bootstrap_rsc.filename ||
                                      "caiwave-bootstrap.rsc"}
                                  </p>
                                </div>

                                <Button
                                  variant="outline"
                                  onClick={() =>
                                    copyToClipboard(
                                      generatedScript.bootstrap_rsc.content
                                    )
                                  }
                                  className="shrink-0 border-neutral-700"
                                >
                                  <Copy className="w-4 h-4 mr-2" />
                                  Copy
                                </Button>
                              </div>

                              <Button
                                onClick={() => downloadBootstrap()}
                                className="w-full bg-blue-600 hover:bg-blue-700"
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Download CAIWAVE Bootstrap
                              </Button>
                            </div>

                            <div className="p-3 rounded-lg bg-neutral-900/50 text-sm text-neutral-400">
                              <p className="font-medium text-neutral-300 mb-1">
                                What happens next?
                              </p>

                              <p>
                                After the bootstrap is imported, CAIWAVE will
                                receive the router's topology information, verify
                                the WAN and client interfaces, generate the
                                production configuration, and send it through the
                                control channel. The production configuration is
                                dry-run validated before it is applied.
                              </p>
                            </div>
                          </div>
                        )}

                      {/* Current Status */}
                      <div className="p-4 bg-neutral-800/50 border border-neutral-800 rounded-lg">
                        <p className="text-xs uppercase tracking-wide text-neutral-500 mb-2">
                          Current Status
                        </p>

                        <p className="text-sm text-neutral-300">
                          {activeProvisioningDiagnostic?.next_action ||
                            provisioningProgress.message ||
                            provisioningMessage ||
                            "Waiting for the router to begin communication with CAIWAVE."}
                        </p>
                      </div>

                      {/* Router Information */}
                      {provisioningRouter && (
                        <div className="grid md:grid-cols-2 gap-4">

                          <div className="p-4 bg-neutral-800/50 rounded-lg">
                            <p className="text-neutral-500 text-sm">
                              Router
                            </p>

                            <p className="font-medium mt-1">
                              {provisioningRouter.router_name || routerName}
                            </p>
                          </div>

                          <div className="p-4 bg-neutral-800/50 rounded-lg">
                            <p className="text-neutral-500 text-sm">
                              NAS Identifier
                            </p>

                            <p className="font-mono text-sm mt-1">
                              {provisioningRouter.nas_identifier || "Pending"}
                            </p>
                          </div>

                        </div>
                      )}

                      {/* Help / Guidance */}
                      <div className="p-4 rounded-lg bg-neutral-800/40 border border-neutral-800">
                        <div className="flex items-start gap-3">

                          <div className="mt-0.5">
                            <Wifi className="w-5 h-5 text-blue-400" />
                          </div>

                          <div>
                            <p className="font-medium">
                              CAIWAVE Guidance
                            </p>

                            <p className="text-sm text-neutral-500 mt-1">
                              Keep the router connected and powered on while
                              provisioning is in progress. CAIWAVE will report
                              the next action and any problem that requires
                              your attention.
                            </p>
                          </div>

                        </div>
                      </div>

                      {/* Close */}
                      <Button
                        onClick={() => {
                          setShowAddRouter(false);
                          setGeneratedScript(null);
                          setProvisioningRouter(null);
                          setProvisioningStep("start");
                          setProvisioningMessage("");
                          setRouterName("");
                          setSelectedHotspot("");
                        }}
                        variant="outline"
                        className="w-full border-neutral-700"
                      >
                        Close
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

// Payments Page Component
const PaymentsPage = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPayments();
  }, []);

  const fetchPayments = async () => {
    try {
      const response = await axios.get(`${API_URL}/payments/`);
      setPayments(response.data);
    } catch (error) {
      console.error("Failed to fetch payments:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div>
        <h1 className="text-2xl font-bold">Payment History</h1>
        <p className="text-neutral-400 mt-1">Track all transactions across your hotspots</p>
      </div>

      <div className="dashboard-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : payments.length === 0 ? (
          <div className="p-8 text-center">
            <CreditCard className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
            <h3 className="font-semibold mb-2">No payments yet</h3>
            <p className="text-neutral-400 text-sm">
              Payments will appear here when users purchase packages
            </p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Phone</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Receipt</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((payment) => (
                <tr key={payment.id}>
                  <td>
                    {new Date(payment.created_at).toLocaleDateString("en-KE", {
                      day: "numeric",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </td>
                  <td className="font-mono">{payment.phone_number}</td>
                  <td className="font-medium">{formatCurrency(payment.amount)}</td>
                  <td>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                        payment.status === "completed"
                          ? "badge-active"
                          : payment.status === "pending"
                          ? "badge-pending"
                          : "badge-inactive"
                      }`}
                    >
                      {payment.status}
                    </span>
                  </td>
                  <td className="font-mono text-sm text-neutral-400">
                    {payment.mpesa_receipt || "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

// Billing & Invoices Page
const BillingPage = () => {
  const [subscription, setSubscription] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingInvoice, setPayingInvoice] = useState(null);

  useEffect(() => {
    fetchBillingData();
  }, []);

  const fetchBillingData = async () => {
    try {
      const token = getAuthToken();
      const headers = { Authorization: `Bearer ${token}` };

      const [subRes, invoicesRes] = await Promise.all([
        axios.get(`${API_URL}/subscriptions/status`, { headers }),
        axios.get(`${API_URL}/invoices/`, { headers }),
      ]);

      setSubscription(subRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      console.error("Failed to fetch billing data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending_discovery: {
        bg: "bg-yellow-500/10",
        text: "text-yellow-400",
        label: "Discovery Pending",
      },
      ready_for_provisioning: {
        bg: "bg-blue-500/10",
        text: "text-blue-400",
        label: "Ready for Provisioning",
      },
      online: {
        bg: "bg-green-500/10",
        text: "text-green-400",
        label: "Online",
      },
      trial: { bg: "bg-blue-500/10", text: "text-blue-400", label: "Trial" },
      unpaid: { bg: "bg-yellow-500/10", text: "text-yellow-400", label: "Unpaid" },
      paid: { bg: "bg-green-500/10", text: "text-green-400", label: "Paid" },
      overdue: { bg: "bg-red-500/10", text: "text-red-400", label: "Overdue" },
    };
    return badges[status] || { bg: "bg-gray-500/10", text: "text-gray-400", label: status };
  };

  const renderDiagnosticItem = (label, passed, detail = "") => (
    <div className="flex items-start gap-2 text-sm">
      <span className={passed ? "text-green-400" : "text-yellow-400"}>
        {passed ? "✓" : "⚠"}
      </span>
      <div>
        <p className={passed ? "text-neutral-200" : "text-neutral-300"}>{label}</p>
        {detail && <p className="text-xs text-neutral-500">{detail}</p>}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Billing & Invoices</h1>
          <p className="text-neutral-400 mt-1">Manage your subscription and view invoices</p>
        </div>
      </div>

      {/* Subscription Summary */}
      {subscription && (
        <div className="dashboard-card">
          <h2 className="font-semibold mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-blue-400" />
            Subscription Status
          </h2>

          <div className="grid md:grid-cols-4 gap-4">
            <div className="p-4 bg-neutral-800/50 rounded-lg">
              <p className="text-neutral-400 text-sm">Status</p>
              <p className="text-lg font-semibold capitalize">{subscription.subscription_status}</p>
            </div>
            <div className="p-4 bg-neutral-800/50 rounded-lg">
              <p className="text-neutral-400 text-sm">Trial Days Left</p>
              <p className="text-lg font-semibold">{subscription.trial_days_remaining}</p>
            </div>
            <div className="p-4 bg-neutral-800/50 rounded-lg">
              <p className="text-neutral-400 text-sm">Hotspots</p>
              <p className="text-lg font-semibold">{subscription.hotspot_count}</p>
            </div>
            <div className="p-4 bg-neutral-800/50 rounded-lg">
              <p className="text-neutral-400 text-sm">Monthly Fee</p>
              <p className="text-lg font-semibold text-green-400">KES {subscription.monthly_fee}</p>
            </div>
          </div>

          {subscription.current_invoice && subscription.current_invoice.status !== "paid" && (
            <div className="mt-4 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg flex items-center justify-between">
              <div>
                <p className="font-medium text-yellow-400">Payment Due</p>
                <p className="text-sm text-neutral-400">
                  Invoice #{subscription.current_invoice.invoice_number} - Due: {new Date(subscription.current_invoice.due_date).toLocaleDateString()}
                </p>
              </div>
              <Button
                onClick={() => setPayingInvoice(subscription.current_invoice)}
                className="bg-yellow-600 hover:bg-yellow-700"
              >
                <CreditCard className="w-4 h-4 mr-2" />
                Pay Now
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Invoice History */}
      <div className="dashboard-card">
        <h2 className="font-semibold mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-400" />
          Invoice History
        </h2>

        {invoices.length === 0 ? (
          <p className="text-neutral-500 text-center py-8">No invoices yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-neutral-800">
                  <th className="text-left py-3 px-4 text-neutral-400 font-medium text-sm">Invoice #</th>
                  <th className="text-left py-3 px-4 text-neutral-400 font-medium text-sm">Period</th>
                  <th className="text-left py-3 px-4 text-neutral-400 font-medium text-sm">Amount</th>
                  <th className="text-left py-3 px-4 text-neutral-400 font-medium text-sm">Status</th>
                  <th className="text-left py-3 px-4 text-neutral-400 font-medium text-sm">Due Date</th>
                  <th className="text-left py-3 px-4 text-neutral-400 font-medium text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => {
                  const badge = getStatusBadge(invoice.status);
                  return (
                    <tr key={invoice.id} className="border-b border-neutral-800/50">
                      <td className="py-3 px-4 font-mono text-sm">{invoice.invoice_number}</td>
                      <td className="py-3 px-4 text-sm">
                        {new Date(invoice.billing_period_start).toLocaleDateString()} - {new Date(invoice.billing_period_end).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4 font-semibold">KES {invoice.amount}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${badge.bg} ${badge.text}`}>
                          {badge.label}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-neutral-400">
                        {new Date(invoice.due_date).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4">
                        {invoice.status !== "paid" && (
                          <Button
                            size="sm"
                            onClick={() => setPayingInvoice(invoice)}
                            className="bg-blue-600 hover:bg-blue-700"
                          >
                            Pay
                          </Button>
                        )}
                        {invoice.status === "paid" && (
                          <span className="text-green-400 text-sm flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" /> Paid
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      {payingInvoice && (
        <PaymentModal
          invoice={payingInvoice}
          onClose={() => setPayingInvoice(null)}
          onSuccess={fetchBillingData}
        />
      )}
    </div>
  );
};

// Voucher Management Page
const EMPTY_VOUCHER_SUMMARY = {
  total: 0,
  unused: 0,
  processing: 0,
  redeemed: 0,
  revoked: 0,
  expired: 0,
};

const VoucherManagementPage = () => {

  const [hotspots, setHotspots] = useState([]);
  const [selectedHotspotId, setSelectedHotspotId] = useState("");
  const [summary, setSummary] = useState(EMPTY_VOUCHER_SUMMARY);
  const [batches, setBatches] = useState([]);
  const [loadingHotspots, setLoadingHotspots] = useState(true);
  const [loadingVouchers, setLoadingVouchers] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [packages, setPackages] = useState([]);
  const [selectedPackageId, setSelectedPackageId] = useState("");
  const [loadingPackages, setLoadingPackages] = useState(true);

  const [generationForm, setGenerationForm] = useState({
    batch_name: "",
    quantity: 1,
    validity_days: 30,
    purpose: "standard",
  });
  const [generatingVouchers, setGeneratingVouchers] = useState(false);
  const [generationError, setGenerationError] = useState("");
  const [generationSuccess, setGenerationSuccess] = useState("");

  const [selectedVoucherBatch, setSelectedVoucherBatch] = useState(null);
  const [batchVouchers, setBatchVouchers] = useState([]);
  const [loadingBatchVouchers, setLoadingBatchVouchers] = useState(false);
  const [batchVoucherError, setBatchVoucherError] = useState("");

  const authHeaders = () => ({
    Authorization: `Bearer ${getAuthToken()}`,
  });

  const fetchHotspots = useCallback(async () => {
    setLoadingHotspots(true);
    setLoadError("");

    try {
      const response = await axios.get(
        `${API_URL}/hotspots/`,
        { headers: authHeaders() }
      );

      const ownerHotspots = Array.isArray(response.data)
        ? response.data
        : [];

      setHotspots(ownerHotspots);

      setSelectedHotspotId((current) => {
        if (
          current &&
          ownerHotspots.some((hotspot) => hotspot.id === current)
        ) {
          return current;
        }

        return ownerHotspots[0]?.id || "";
      });
    } catch (error) {
      setLoadError(safeError(error));
      setHotspots([]);
      setSelectedHotspotId("");
    } finally {
      setLoadingHotspots(false);
    }
  }, []);

  const fetchPackages = useCallback(async () => {
    setLoadingPackages(true);
    setGenerationError("");

    try {
      const response = await axios.get(
        `${API_URL}/packages/`,
        { headers: authHeaders() }
      );

      const activePackages = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.packages)
          ? response.data.packages
          : [];

      setPackages(activePackages);

      setSelectedPackageId((current) => {
        if (
          current &&
          activePackages.some((item) => item.id === current)
        ) {
          return current;
        }

        return activePackages[0]?.id || "";
      });
    } catch (error) {
      setGenerationError(safeError(error));
      setPackages([]);
      setSelectedPackageId("");
    } finally {
      setLoadingPackages(false);
    }
  }, []);

  const fetchVoucherData = useCallback(async (hotspotId) => {
    if (!hotspotId) {
      setSummary(EMPTY_VOUCHER_SUMMARY);
      setBatches([]);
      return;
    }

    setLoadingVouchers(true);
    setLoadError("");

    try {
      const headers = authHeaders();
      const params = { hotspot_id: hotspotId };

      const [summaryResponse, batchesResponse] = await Promise.all([
        axios.get(`${API_URL}/vouchers/summary`, {
          headers,
          params,
        }),
        axios.get(`${API_URL}/vouchers/batches`, {
          headers,
          params,
        }),
      ]);

      setSummary({
        ...EMPTY_VOUCHER_SUMMARY,
        ...(summaryResponse.data || {}),
      });

      setBatches(
        Array.isArray(batchesResponse.data)
          ? batchesResponse.data
          : []
      );
    } catch (error) {
      setLoadError(safeError(error));
      setSummary(EMPTY_VOUCHER_SUMMARY);
      setBatches([]);
    } finally {
      setLoadingVouchers(false);
    }
  }, []);

  useEffect(() => {
    fetchHotspots();
  }, [fetchHotspots]);

  useEffect(() => {
    fetchPackages();
  }, [fetchPackages]);

  useEffect(() => {
    fetchVoucherData(selectedHotspotId);
  }, [fetchVoucherData, selectedHotspotId]);

  const selectedHotspot = hotspots.find(
    (hotspot) => hotspot.id === selectedHotspotId
  );

  const selectedPackage = packages.find(
    (item) => item.id === selectedPackageId
  );

  const updateGenerationForm = (field, value) => {
    setGenerationForm((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleGenerateVouchers = async (event) => {
    event.preventDefault();

    setGenerationError("");
    setGenerationSuccess("");

    if (!selectedHotspotId) {
      setGenerationError("Select a hotspot before generating vouchers.");
      return;
    }

    if (!selectedPackageId) {
      setGenerationError("Select a WiFi package before generating vouchers.");
      return;
    }

    const quantity = Number(generationForm.quantity);
    const validityDays = Number(generationForm.validity_days);

    if (!Number.isInteger(quantity) || quantity < 1 || quantity > 1000) {
      setGenerationError(
        "Voucher quantity must be a whole number between 1 and 1000."
      );
      return;
    }

    if (
      !Number.isInteger(validityDays) ||
      validityDays < 1 ||
      validityDays > 365
    ) {
      setGenerationError(
        "Voucher validity must be a whole number between 1 and 365 days."
      );
      return;
    }

    setGeneratingVouchers(true);

    try {
      const response = await axios.post(
        `${API_URL}/vouchers/generate`,
        {
          package_id: selectedPackageId,
          hotspot_id: selectedHotspotId,
          quantity,
          validity_days: validityDays,
          purpose: generationForm.purpose,
          batch_name: generationForm.batch_name.trim() || null,
        },
        {
          headers: authHeaders(),
        }
      );

      const generatedCount = Array.isArray(response.data)
        ? response.data.length
        : quantity;

      setGenerationSuccess(
        `${generatedCount} voucher${
          generatedCount === 1 ? "" : "s"
        } generated successfully.`
      );

      setGenerationForm({
        batch_name: "",
        quantity: 1,
        validity_days: 30,
        purpose: "standard",
      });

      await fetchVoucherData(selectedHotspotId);
    } catch (error) {
      setGenerationError(safeError(error));
    } finally {
      setGeneratingVouchers(false);
    }
  };

  const closeVoucherBatchViewer = () => {
    setSelectedVoucherBatch(null);
    setBatchVouchers([]);
    setBatchVoucherError("");
    setLoadingBatchVouchers(false);
  };

  const openVoucherBatchViewer = async (batch) => {
    setSelectedVoucherBatch(batch);
    setBatchVouchers([]);
    setBatchVoucherError("");
    setLoadingBatchVouchers(true);

    try {
      const response = await axios.get(
        `${API_URL}/vouchers/`,
        {
          headers: authHeaders(),
          params: {
            hotspot_id: selectedHotspotId,
            batch_id: batch.batch_id,
          },
        }
      );

      setBatchVouchers(
        Array.isArray(response.data)
          ? response.data
          : []
      );
    } catch (error) {
      setBatchVoucherError(safeError(error));
      setBatchVouchers([]);
    } finally {
      setLoadingBatchVouchers(false);
    }
  };

  const getVoucherDisplayStatus = (voucher) => {
    const status = voucher.redemption_status || (
      voucher.is_used ? "redeemed" : "unused"
    );

    if (
      status === "unused" &&
      voucher.expires_at &&
      new Date(voucher.expires_at).getTime() <= Date.now()
    ) {
      return "expired";
    }

    return status;
  };

  const getVoucherStatusClassName = (status) => {
    switch (status) {
      case "redeemed":
        return "bg-purple-500/10 text-purple-300";
      case "revoked":
        return "bg-red-500/10 text-red-300";
      case "expired":
        return "bg-orange-500/10 text-orange-300";
      case "processing":
        return "bg-blue-500/10 text-blue-300";
      default:
        return "bg-green-500/10 text-green-300";
    }
  };

  const printVoucherBatch = () => {
    if (!selectedVoucherBatch || batchVouchers.length === 0) {
      return;
    }

    const escapeHtml = (value) =>
      String(value ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");

    const batchPackage = packages.find(
      (item) =>
        item.id === selectedVoucherBatch.package_id
    );

    const hotspotName =
      selectedHotspot?.name ||
      selectedHotspot?.ssid ||
      selectedHotspotId ||
      "CAIWAVE Hotspot";

    const hotspotSsid =
      selectedHotspot?.ssid || hotspotName;

    const packageName =
      batchPackage?.name ||
      selectedVoucherBatch.package_id ||
      "WiFi Package";

    const packageDetails = [
      batchPackage?.duration_minutes
        ? `${batchPackage.duration_minutes} minutes`
        : null,
      batchPackage?.speed_mbps
        ? `${batchPackage.speed_mbps} Mbps`
        : null,
      batchPackage?.data_limit_mb
        ? `${batchPackage.data_limit_mb} MB`
        : "Unlimited data",
    ]
      .filter(Boolean)
      .join(" • ");

    const voucherCards = batchVouchers
      .map((voucher) => {
        const status = getVoucherDisplayStatus(voucher);

        return `
          <article class="voucher-card">
            <header class="voucher-header">
              <div>
                <div class="brand">CAIWAVE WiFi</div>
                <div class="hotspot">${escapeHtml(hotspotName)}</div>
              </div>
              <div class="status">${escapeHtml(status)}</div>
            </header>

            <section class="voucher-body">
              <div class="access-code-label">Voucher Code</div>
              <div class="access-code">${escapeHtml(voucher.code)}</div>

              <div class="credentials">
                <div class="credential">
                  <span>Username</span>
                  <strong>${escapeHtml(voucher.username)}</strong>
                </div>

                <div class="credential">
                  <span>Password</span>
                  <strong>${escapeHtml(voucher.password)}</strong>
                </div>
              </div>

              <div class="instructions">
                Join <strong>${escapeHtml(hotspotSsid)}</strong>, open the
                CAIWAVE portal, and enter this voucher code.
              </div>
            </section>

            <footer class="voucher-footer">
              <div>
                <span>Package</span>
                <strong>${escapeHtml(packageName)}</strong>
              </div>

              <div>
                <span>Expires</span>
                <strong>${escapeHtml(
                  formatVoucherDate(voucher.expires_at)
                )}</strong>
              </div>

              <div class="batch-reference">
                Batch: ${escapeHtml(
                  selectedVoucherBatch.batch_name ||
                    selectedVoucherBatch.batch_id
                )}
              </div>
            </footer>
          </article>
        `;
      })
      .join("");

    const printWindow = window.open(
      "",
      "_blank",
      "width=1100,height=800"
    );

    if (!printWindow) {
      toast.error(
        "The print window was blocked. Allow pop-ups and try again."
      );
      return;
    }

    printWindow.opener = null;

    printWindow.document.write(`
      <!doctype html>
      <html lang="en">
        <head>
          <meta charset="utf-8" />
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1"
          />
          <title>${escapeHtml(
            selectedVoucherBatch.batch_name ||
              "CAIWAVE Voucher Batch"
          )}</title>

          <style>
            * {
              box-sizing: border-box;
            }

            body {
              margin: 0;
              padding: 24px;
              background: #f3f4f6;
              color: #111827;
              font-family:
                Arial,
                Helvetica,
                sans-serif;
            }

            .document-header {
              max-width: 1100px;
              margin: 0 auto 20px;
            }

            .document-header h1 {
              margin: 0;
              font-size: 24px;
            }

            .document-header p {
              margin: 6px 0 0;
              color: #4b5563;
              font-size: 14px;
            }

            .voucher-grid {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 14px;
              max-width: 1100px;
              margin: 0 auto;
            }

            .voucher-card {
              display: flex;
              min-height: 310px;
              flex-direction: column;
              overflow: hidden;
              border: 2px solid #111827;
              border-radius: 14px;
              background: #ffffff;
              break-inside: avoid;
              page-break-inside: avoid;
            }

            .voucher-header {
              display: flex;
              align-items: flex-start;
              justify-content: space-between;
              gap: 16px;
              padding: 18px 20px;
              background: #111827;
              color: #ffffff;
            }

            .brand {
              font-size: 19px;
              font-weight: 800;
              letter-spacing: 0.04em;
            }

            .hotspot {
              margin-top: 4px;
              color: #d1d5db;
              font-size: 13px;
            }

            .status {
              border: 1px solid #6b7280;
              border-radius: 999px;
              padding: 5px 9px;
              font-size: 11px;
              font-weight: 700;
              text-transform: uppercase;
            }

            .voucher-body {
              flex: 1;
              padding: 22px 20px;
            }

            .access-code-label {
              color: #6b7280;
              font-size: 11px;
              font-weight: 700;
              letter-spacing: 0.12em;
              text-align: center;
              text-transform: uppercase;
            }

            .access-code {
              margin-top: 7px;
              font-family:
                "Courier New",
                monospace;
              font-size: 30px;
              font-weight: 800;
              letter-spacing: 0.12em;
              text-align: center;
            }

            .credentials {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 12px;
              margin-top: 22px;
            }

            .credential {
              border: 1px solid #d1d5db;
              border-radius: 9px;
              padding: 11px;
            }

            .credential span,
            .voucher-footer span {
              display: block;
              color: #6b7280;
              font-size: 10px;
              font-weight: 700;
              letter-spacing: 0.08em;
              text-transform: uppercase;
            }

            .credential strong {
              display: block;
              margin-top: 5px;
              font-family:
                "Courier New",
                monospace;
              font-size: 14px;
              overflow-wrap: anywhere;
            }

            .instructions {
              margin-top: 18px;
              color: #4b5563;
              font-size: 12px;
              line-height: 1.5;
              text-align: center;
            }

            .voucher-footer {
              display: grid;
              grid-template-columns: repeat(2, minmax(0, 1fr));
              gap: 10px;
              border-top: 1px dashed #9ca3af;
              padding: 14px 20px;
              background: #f9fafb;
              font-size: 11px;
            }

            .voucher-footer strong {
              display: block;
              margin-top: 4px;
              font-size: 11px;
            }

            .batch-reference {
              grid-column: 1 / -1;
              color: #6b7280;
              font-size: 9px;
              overflow-wrap: anywhere;
            }

            @media print {
              @page {
                size: A4;
                margin: 10mm;
              }

              body {
                padding: 0;
                background: #ffffff;
              }

              .document-header {
                margin-bottom: 12px;
              }

              .voucher-grid {
                gap: 8mm;
              }
            }

            @media screen and (max-width: 760px) {
              .voucher-grid {
                grid-template-columns: 1fr;
              }
            }
          </style>
        </head>

        <body>
          <header class="document-header">
            <h1>${escapeHtml(
              selectedVoucherBatch.batch_name ||
                "CAIWAVE Voucher Batch"
            )}</h1>

            <p>
              ${batchVouchers.length} voucher${
                batchVouchers.length === 1 ? "" : "s"
              }
              • ${escapeHtml(hotspotName)}
              • ${escapeHtml(packageName)}
              ${
                packageDetails
                  ? `• ${escapeHtml(packageDetails)}`
                  : ""
              }
            </p>
          </header>

          <main class="voucher-grid">
            ${voucherCards}
          </main>

          <script>
            window.addEventListener("load", () => {
              window.focus();
              window.print();
            });
          </script>
        </body>
      </html>
    `);

    printWindow.document.close();
  };

  const summaryCards = [
    {
      label: "Total",
      value: summary.total,
      icon: Ticket,
      className: "text-blue-400",
    },
    {
      label: "Unused",
      value: summary.unused,
      icon: CheckCircle,
      className: "text-green-400",
    },
    {
      label: "Redeemed",
      value: summary.redeemed,
      icon: Wifi,
      className: "text-purple-400",
    },
    {
      label: "Revoked",
      value: summary.revoked,
      icon: Ban,
      className: "text-red-400",
    },
    {
      label: "Expired",
      value: summary.expired,
      icon: Clock,
      className: "text-orange-400",
    },
  ];

  const formatVoucherDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "—";
    }

    return date.toLocaleString();
  };

  return (
    <div>
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Voucher Management</h1>
          <p className="mt-1 text-neutral-400">
            Generate, manage, and track prepaid WiFi vouchers for your hotspots.
          </p>
        </div>

        <Button
          type="button"
          variant="outline"
          className="border-neutral-700"
          onClick={() => {
            fetchHotspots();
            fetchPackages();

            if (selectedHotspotId) {
              fetchVoucherData(selectedHotspotId);
            }
          }}
          disabled={loadingHotspots || loadingVouchers}
        >
          <RefreshCw
            className={`mr-2 h-4 w-4 ${
              loadingHotspots || loadingVouchers
                ? "animate-spin"
                : ""
            }`}
          />
          Refresh
        </Button>
      </div>

      {loadError && (
        <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 text-red-400" />
            <div>
              <p className="font-medium text-red-300">
                Voucher data could not be loaded
              </p>
              <p className="mt-1 text-sm text-red-200/70">
                {loadError}
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6 rounded-xl border border-neutral-800 bg-neutral-900 p-5">
        <label
          htmlFor="voucher-hotspot"
          className="mb-2 block text-sm font-medium text-neutral-300"
        >
          Hotspot
        </label>

        {loadingHotspots ? (
          <div className="flex items-center gap-2 text-sm text-neutral-400">
            <RefreshCw className="h-4 w-4 animate-spin" />
            Loading hotspots…
          </div>
        ) : hotspots.length === 0 ? (
          <div className="rounded-lg border border-dashed border-neutral-700 p-5 text-sm text-neutral-400">
            No hotspots are available. Create and activate a hotspot before
            generating vouchers.
          </div>
        ) : (
          <>
            <select
              id="voucher-hotspot"
              value={selectedHotspotId}
              onChange={(event) =>
                setSelectedHotspotId(event.target.value)
              }
              className="w-full rounded-lg border border-neutral-700 bg-neutral-950 px-4 py-3 text-white outline-none focus:border-blue-500"
            >
              {hotspots.map((hotspot) => (
                <option key={hotspot.id} value={hotspot.id}>
                  {hotspot.name || hotspot.ssid || hotspot.id}
                </option>
              ))}
            </select>

            {selectedHotspot && (
              <p className="mt-2 text-xs text-neutral-500">
                {selectedHotspot.ssid
                  ? `SSID: ${selectedHotspot.ssid}`
                  : "Selected hotspot"}
                {selectedHotspot.location_name
                  ? ` • ${selectedHotspot.location_name}`
                  : ""}
              </p>
            )}
          </>
        )}
      </div>

      <div className="mb-8 rounded-xl border border-neutral-800 bg-neutral-900 p-5">
        <div className="mb-5">
          <h2 className="font-semibold">Generate Voucher Batch</h2>
          <p className="mt-1 text-sm text-neutral-400">
            Create prepaid vouchers using one of the active WiFi packages.
          </p>
        </div>

        {generationError && (
          <div className="mb-5 rounded-lg border border-red-500/30 bg-red-500/10 p-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-5 w-5 text-red-400" />
              <p className="text-sm text-red-200">
                {generationError}
              </p>
            </div>
          </div>
        )}

        {generationSuccess && (
          <div className="mb-5 rounded-lg border border-green-500/30 bg-green-500/10 p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="mt-0.5 h-5 w-5 text-green-400" />
              <p className="text-sm text-green-200">
                {generationSuccess}
              </p>
            </div>
          </div>
        )}

        <form onSubmit={handleGenerateVouchers}>
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            <div>
              <label
                htmlFor="voucher-package"
                className="mb-2 block text-sm font-medium text-neutral-300"
              >
                WiFi Package
              </label>

              <select
                id="voucher-package"
                value={selectedPackageId}
                onChange={(event) => {
                  setSelectedPackageId(event.target.value);
                  setGenerationError("");
                  setGenerationSuccess("");
                }}
                disabled={loadingPackages || packages.length === 0}
                className="w-full rounded-lg border border-neutral-700 bg-neutral-950 px-4 py-3 text-white outline-none focus:border-blue-500 disabled:cursor-not-allowed disabled:opacity-60"
                required
              >
                {loadingPackages ? (
                  <option value="">Loading packages…</option>
                ) : packages.length === 0 ? (
                  <option value="">No active packages available</option>
                ) : (
                  packages.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name} — KES {item.price}
                    </option>
                  ))
                )}
              </select>

              {selectedPackage && (
                <p className="mt-2 text-xs text-neutral-500">
                  {selectedPackage.duration_minutes} minutes
                  {" • "}
                  {selectedPackage.speed_mbps} Mbps
                  {selectedPackage.data_limit_mb
                    ? ` • ${selectedPackage.data_limit_mb} MB`
                    : " • Unlimited data"}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="voucher-batch-name"
                className="mb-2 block text-sm font-medium text-neutral-300"
              >
                Batch Name
              </label>

              <input
                id="voucher-batch-name"
                type="text"
                maxLength={120}
                value={generationForm.batch_name}
                onChange={(event) =>
                  updateGenerationForm("batch_name", event.target.value)
                }
                placeholder="Example: Weekend vouchers"
                className="w-full rounded-lg border border-neutral-700 bg-neutral-950 px-4 py-3 text-white outline-none placeholder:text-neutral-600 focus:border-blue-500"
              />

              <p className="mt-2 text-xs text-neutral-500">
                Optional administrative name for this batch.
              </p>
            </div>

            <div>
              <label
                htmlFor="voucher-purpose"
                className="mb-2 block text-sm font-medium text-neutral-300"
              >
                Purpose
              </label>

              <select
                id="voucher-purpose"
                value={generationForm.purpose}
                onChange={(event) =>
                  updateGenerationForm("purpose", event.target.value)
                }
                className="w-full rounded-lg border border-neutral-700 bg-neutral-950 px-4 py-3 text-white outline-none focus:border-blue-500"
              >
                <option value="standard">Standard</option>
                <option value="test">Test</option>
                <option value="compensation">Compensation</option>
                <option value="promotion">Promotion</option>
                <option value="staff">Staff</option>
                <option value="offline_sale">Offline sale</option>
              </select>
            </div>

            <div>
              <label
                htmlFor="voucher-quantity"
                className="mb-2 block text-sm font-medium text-neutral-300"
              >
                Quantity
              </label>

              <input
                id="voucher-quantity"
                type="number"
                min="1"
                max="1000"
                step="1"
                value={generationForm.quantity}
                onChange={(event) =>
                  updateGenerationForm("quantity", event.target.value)
                }
                className="w-full rounded-lg border border-neutral-700 bg-neutral-950 px-4 py-3 text-white outline-none focus:border-blue-500"
                required
              />

              <p className="mt-2 text-xs text-neutral-500">
                Between 1 and 1,000 vouchers.
              </p>
            </div>

            <div>
              <label
                htmlFor="voucher-validity"
                className="mb-2 block text-sm font-medium text-neutral-300"
              >
                Validity in Days
              </label>

              <input
                id="voucher-validity"
                type="number"
                min="1"
                max="365"
                step="1"
                value={generationForm.validity_days}
                onChange={(event) =>
                  updateGenerationForm(
                    "validity_days",
                    event.target.value
                  )
                }
                className="w-full rounded-lg border border-neutral-700 bg-neutral-950 px-4 py-3 text-white outline-none focus:border-blue-500"
                required
              />

              <p className="mt-2 text-xs text-neutral-500">
                Time allowed before unused vouchers expire.
              </p>
            </div>

            <div className="flex items-end">
              <Button
                type="submit"
                className="w-full"
                disabled={
                  generatingVouchers ||
                  loadingPackages ||
                  loadingHotspots ||
                  !selectedHotspotId ||
                  !selectedPackageId
                }
              >
                {generatingVouchers ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Generating…
                  </>
                ) : (
                  <>
                    <Ticket className="mr-2 h-4 w-4" />
                    Generate Vouchers
                  </>
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>

      <div className="mb-8 grid gap-4 sm:grid-cols-2 xl:grid-cols-5">
        {summaryCards.map((card) => (
          <div
            key={card.label}
            className="rounded-xl border border-neutral-800 bg-neutral-900 p-5"
          >
            <div className="flex items-center justify-between">
              <p className="text-sm text-neutral-400">
                {card.label}
              </p>
              <card.icon className={`h-5 w-5 ${card.className}`} />
            </div>

            <p className="mt-3 text-3xl font-bold">
              {loadingVouchers ? "—" : card.value}
            </p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-xl border border-neutral-800 bg-neutral-900">
        <div className="border-b border-neutral-800 px-5 py-4">
          <h2 className="font-semibold">Voucher Batches</h2>
          <p className="mt-1 text-sm text-neutral-400">
            Voucher batches created for the selected hotspot.
          </p>
        </div>

        {loadingVouchers ? (
          <div className="flex items-center justify-center gap-3 p-12 text-neutral-400">
            <RefreshCw className="h-5 w-5 animate-spin" />
            Loading voucher batches…
          </div>
        ) : !selectedHotspotId ? (
          <div className="p-12 text-center text-neutral-400">
            Select a hotspot to view voucher batches.
          </div>
        ) : batches.length === 0 ? (
          <div className="p-12 text-center">
            <Ticket className="mx-auto mb-4 h-10 w-10 text-neutral-600" />
            <h3 className="font-medium">No voucher batches yet</h3>
            <p className="mt-1 text-sm text-neutral-500">
              Generated voucher batches will appear here.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-neutral-800">
              <thead className="bg-neutral-950/60">
                <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                  <th className="px-5 py-3">Batch</th>
                  <th className="px-5 py-3">Purpose</th>
                  <th className="px-5 py-3">Created</th>
                  <th className="px-5 py-3">Total</th>
                  <th className="px-5 py-3">Unused</th>
                  <th className="px-5 py-3">Redeemed</th>
                  <th className="px-5 py-3">Revoked</th>
                  <th className="px-5 py-3">Expired</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-neutral-800">
                {batches.map((batch) => (
                  <tr
                    key={batch.batch_id}
                    className="text-sm hover:bg-neutral-800/40"
                  >
                    <td className="px-5 py-4">
                      <p className="font-medium text-white">
                        {batch.batch_name || "Unnamed batch"}
                      </p>
                      <p className="mt-1 font-mono text-xs text-neutral-500">
                        {batch.batch_id}
                      </p>
                    </td>
                    <td className="px-5 py-4 capitalize text-neutral-300">
                      {(batch.purpose || "standard").replaceAll("_", " ")}
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-neutral-400">
                      {formatVoucherDate(batch.created_at)}
                    </td>
                    <td className="px-5 py-4 font-medium">
                      {batch.total}
                    </td>
                    <td className="px-5 py-4 text-green-400">
                      {batch.unused}
                    </td>
                    <td className="px-5 py-4 text-purple-400">
                      {batch.redeemed}
                    </td>
                    <td className="px-5 py-4 text-red-400">
                      {batch.revoked}
                    </td>
                    <td className="px-5 py-4 text-orange-400">
                      {batch.expired}
                    </td>
                    <td className="whitespace-nowrap px-5 py-4 text-right">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="border-neutral-700"
                        onClick={() => openVoucherBatchViewer(batch)}
                      >
                        <Ticket className="mr-2 h-4 w-4" />
                        View Vouchers
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {selectedVoucherBatch && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/75 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="voucher-batch-dialog-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeVoucherBatchViewer();
            }
          }}
        >
          <div className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-neutral-700 bg-neutral-900 shadow-2xl">
            <div className="flex items-start justify-between border-b border-neutral-800 px-5 py-4">
              <div>
                <h2
                  id="voucher-batch-dialog-title"
                  className="text-lg font-semibold"
                >
                  {selectedVoucherBatch.batch_name || "Unnamed batch"}
                </h2>
                <p className="mt-1 text-sm text-neutral-400">
                  {selectedVoucherBatch.total} voucher
                  {selectedVoucherBatch.total === 1 ? "" : "s"}
                  {" • "}
                  {(selectedVoucherBatch.purpose || "standard")
                    .replaceAll("_", " ")}
                </p>
                <p className="mt-1 font-mono text-xs text-neutral-600">
                  {selectedVoucherBatch.batch_id}
                </p>
              </div>

              <button
                type="button"
                onClick={closeVoucherBatchViewer}
                className="rounded-lg p-2 text-neutral-400 transition-colors hover:bg-neutral-800 hover:text-white"
                aria-label="Close voucher batch"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-auto">
              {loadingBatchVouchers ? (
                <div className="flex items-center justify-center gap-3 p-16 text-neutral-400">
                  <RefreshCw className="h-5 w-5 animate-spin" />
                  Loading voucher credentials…
                </div>
              ) : batchVoucherError ? (
                <div className="p-6">
                  <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="mt-0.5 h-5 w-5 text-red-400" />
                      <div>
                        <p className="font-medium text-red-300">
                          Voucher credentials could not be loaded
                        </p>
                        <p className="mt-1 text-sm text-red-200/70">
                          {batchVoucherError}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              ) : batchVouchers.length === 0 ? (
                <div className="p-16 text-center">
                  <Ticket className="mx-auto mb-4 h-10 w-10 text-neutral-600" />
                  <h3 className="font-medium">
                    No vouchers found
                  </h3>
                  <p className="mt-1 text-sm text-neutral-500">
                    This batch does not contain any accessible vouchers.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-neutral-800">
                    <thead className="sticky top-0 bg-neutral-950">
                      <tr className="text-left text-xs uppercase tracking-wide text-neutral-500">
                        <th className="px-5 py-3">Voucher Code</th>
                        <th className="px-5 py-3">Username</th>
                        <th className="px-5 py-3">Password</th>
                        <th className="px-5 py-3">Status</th>
                        <th className="px-5 py-3">Expires</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-neutral-800">
                      {batchVouchers.map((voucher) => {
                        const displayStatus =
                          getVoucherDisplayStatus(voucher);

                        return (
                          <tr
                            key={voucher.id}
                            className="text-sm hover:bg-neutral-800/40"
                          >
                            <td className="px-5 py-4">
                              <span className="font-mono font-semibold text-white">
                                {voucher.code}
                              </span>
                            </td>
                            <td className="px-5 py-4">
                              <span className="font-mono text-neutral-200">
                                {voucher.username}
                              </span>
                            </td>
                            <td className="px-5 py-4">
                              <span className="font-mono text-neutral-200">
                                {voucher.password}
                              </span>
                            </td>
                            <td className="px-5 py-4">
                              <span
                                className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium capitalize ${getVoucherStatusClassName(
                                  displayStatus
                                )}`}
                              >
                                {displayStatus}
                              </span>
                            </td>
                            <td className="whitespace-nowrap px-5 py-4 text-neutral-400">
                              {formatVoucherDate(voucher.expires_at)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-neutral-800 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <Button
                type="button"
                variant="outline"
                className="border-neutral-700"
                onClick={printVoucherBatch}
                disabled={
                  loadingBatchVouchers ||
                  batchVouchers.length === 0
                }
              >
                <Printer className="mr-2 h-4 w-4" />
                Print / Save PDF
              </Button>

              <Button
                type="button"
                variant="outline"
                className="border-neutral-700"
                onClick={closeVoucherBatchViewer}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Main Dashboard Layout
const OwnerDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const user = getUser();

  const navigation = [
    { name: "Overview", href: "/owner", icon: LayoutDashboard },
    { name: "Hotspots", href: "/owner/hotspots", icon: Radio },
    { name: "Vouchers", href: "/owner/vouchers", icon: Ticket },
    { name: "MikroTik", href: "/owner/mikrotik", icon: Zap },
    { name: "Billing", href: "/owner/billing", icon: FileText },
    { name: "Payments", href: "/owner/payments", icon: CreditCard },
    { name: "Payouts", href: "/owner/payouts", icon: Wallet },
    { name: "Analytics", href: "/owner/analytics", icon: BarChart3 },
    { name: "Settings", href: "/owner/settings", icon: Settings },
  ];

  const handleLogout = () => {
    logout();
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 sidebar transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center gap-2 px-6 border-b border-neutral-800">
            <CaiwaveLogo size={32} />
            <span className="font-semibold text-lg">CAIWAVE</span>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navigation.map((item) => {
              const isActive =
                location.pathname === item.href ||
                (item.href !== "/owner" && location.pathname.startsWith(item.href));
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    isActive
                      ? "bg-blue-600/10 text-blue-500"
                      : "text-neutral-400 hover:text-white hover:bg-neutral-800"
                  }`}
                >
                  <item.icon className="w-5 h-5" strokeWidth={1.5} />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-neutral-800">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-neutral-800 rounded-full flex items-center justify-center">
                <span className="font-medium text-sm">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user?.name}</p>
                <p className="text-xs text-neutral-400 truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="w-full border-neutral-700 text-neutral-400 hover:text-white"
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 lg:pl-64">
        {/* Top Bar */}
        <header className="h-16 border-b border-neutral-800 flex items-center justify-between px-6">
          <button
            className="lg:hidden p-2 -ml-2"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-6 h-6" />
          </button>

          <div className="flex items-center gap-4 ml-auto">
            {user?.role === ROLES.SUPER_ADMIN && (
              <Link to="/admin">
                <Button variant="outline" size="sm" className="border-neutral-700">
                  Admin Panel
                </Button>
              </Link>
            )}
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6 lg:p-8">
          <Routes>
            <Route index element={<DashboardOverview />} />
            <Route path="hotspots" element={<HotspotsPage />} />
            <Route path="vouchers" element={<VoucherManagementPage />} />
            <Route path="mikrotik" element={<MikroTikSetupPage />} />
            <Route path="billing" element={<BillingPage />} />
            <Route path="payments" element={<PaymentsPage />} />
            <Route
              path="analytics"
              element={
                <div className="text-center py-12">
                  <BarChart3 className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Analytics</h2>
                  <p className="text-neutral-400">Detailed analytics coming soon</p>
                </div>
              }
            />
            <Route
              path="payouts"
              element={<PaymentSettings />}
            />
            <Route
              path="settings"
              element={
                <div className="text-center py-12">
                  <Settings className="w-12 h-12 text-neutral-600 mx-auto mb-4" />
                  <h2 className="text-xl font-semibold mb-2">Settings</h2>
                  <p className="text-neutral-400">Account settings coming soon</p>
                </div>
              }
            />
          </Routes>
        </main>

        {/* Footer */}
        <footer className="p-4 text-center border-t border-neutral-800">
          <p className="text-neutral-500 text-xs">
            Powered by <span className="text-blue-400 font-medium">CAIWAVE WiFi</span> © 2026. All Rights Reserved.
            <span className="mx-2">|</span>
            <a href="tel:0738570630" className="text-neutral-500 hover:text-blue-400 transition-colors">Support</a>
          </p>
        </footer>
      </div>
    </div>
  );
};

export default OwnerDashboard;
